// API composable pro CRUD metody
// import * as Sentry from '@sentry/vue'

import QueryString from 'qs'
import type { ApiKeys } from '@@/shared-components/types/Api'
import { appendServerProfilerLog, estimateValueSizeBytes } from './profilerLogger'

export function useApi(localeProps?: Ref<string>, dataLangProps?: Ref<string>) {
  const abortController = shallowRef(new AbortController())
  const instance = getCurrentInstance()
  if (instance) {
    onErrorCaptured((e) => {
      if (e?.message?.includes('aborted')) return false
    })
    onUnmounted(() => {
      if (!router.currentRoute.value?.path?.startsWith('/kiosk')) {
        abortController.value?.abort()
      }
    })
  }
  const router = useRouter()
  const {
    $i18n: { t },
    $sharedComponentsOptions,
  } = useNuxtApp()
  const { addWarningMessage, addErrorMessage } = useToastService()
  const { $auth } = useNuxtApp()
  const auth = useAuth()
  const { locale, dataLang } = localeProps
    ? { locale: localeProps, dataLang: dataLangProps }
    : useLocale()
  const { channel } = useChannel()
  const {
    recaptchaV2Key,
    executeV3,
    apiKeysPromise,
    load: loadRecaptcha,
    recaptchaV2ApiKey,
    recaptchaV3ApiKey,
    passThrowRecaptchaV3,
  } = useRecaptcha()
  const { public: config } = useRuntimeConfig()

  const fetch = async <Type, Meta>(
    method: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE',
    url: string,
    options: {
      origin?: string
      params?: string
      data?: object
      headerOptions?: { [key: string]: string } | object
      signal?: AbortSignal
      fullResponse?: boolean
      recaptchaActionName?: string
      customErrorHandling?: boolean
    }
  ) => {
    const startedAt = import.meta.server ? Date.now() : 0
    const {
      params = null,
      data = null,
      headerOptions = null,
      signal = undefined,
      fullResponse = false,
      recaptchaActionName = undefined,
      customErrorHandling = false,
    } = options

    let headers: { [index: string]: string } = {
      Accept: 'application/json',
    }
    if (locale.value) {
      headers['X-Lang'] = locale.value
    }
    if (dataLang?.value) {
      headers['X-Data-Lang'] = dataLang.value
    }
    if (channel.value) {
      headers['X-Channel'] = channel.value
    }
    // Authorization strategy:
    // - JWT mode (default): send Bearer token when available
    // - Sanctum SPA mode: no token cookie, send X-XSRF-TOKEN for
    //   state-changing requests instead
    if (auth.value?.token) {
      headers['Authorization'] = `Bearer ${auth.value?.token}`
    } else if (
      $sharedComponentsOptions?.options?.auth?.sanctumSpa &&
      ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
    ) {
      const xsrfToken = useCookie('XSRF-TOKEN', {
        readonly: true,
        decode: decodeURIComponent,
      }).value
      if (xsrfToken) headers['X-XSRF-TOKEN'] = xsrfToken
    }
    if (import.meta.client) {
      headers['X-Client-Page'] = window.location.pathname
    }

    if (recaptchaActionName) {
      if (!apiKeysPromise.value && (!recaptchaV3ApiKey.value || !!recaptchaV2ApiKey.value)) {
        const getRecaptchaApi3Key = new Promise((resolve) => {
          apiKeysPromise.value = get<ApiKeys>('/public/settings/api-keys').then(({ data }) => {
            recaptchaV2ApiKey.value = data.recaptcha_v2_site_key.value
            recaptchaV3ApiKey.value = data.recaptcha_site_key.value
            resolve(data)
          })
        })
        await getRecaptchaApi3Key
      }
      if (recaptchaV3ApiKey.value) {
        await loadRecaptcha(recaptchaV3ApiKey.value)
      } else {
        if (!recaptchaV3ApiKey.value) {
          console.warn('No Google Recaptcha v3 site key provided')
        }
        if (!recaptchaV2ApiKey.value) {
          console.warn('No Google Recaptcha v2 site key provided')
        }
      }
      const recaptchaToken =
        recaptchaV2Key.value ?? ((await executeV3(recaptchaActionName)) as string)
      const recaptchaVersionName = recaptchaV2Key.value
        ? 'X-Google-Recaptcha-V2-Token'
        : 'X-Google-Recaptcha-V3-Token'

      headers[recaptchaVersionName] = recaptchaToken
    }
    if (headerOptions) {
      headers = { ...headers, ...headerOptions }
    }
    let fullRes = null
    // Sentry.addBreadcrumb({
    //   category: 'http',
    //   message: `Fetching ${url}`,
    //   level: 'info',
    //   data: {
    //     method,
    //     headers,
    //     body: data,
    //   },
    // })
    const apiBaseUrl =
      options.origin ?? (config.apiBaseUrl ? config.apiBaseUrl : '$NUXT_PUBLIC_API_BASE_URL')
    const requestURL = `${apiBaseUrl}${url}${params ? `?${params}` : ''}`
    let response

    try {
      response = await $fetch<{
        data: Type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        meta: { total?: number; hasMorePages?: boolean; validationErrors?: any[] } & Meta
      }>(requestURL, {
        method,
        body: data,
        headers,
        signal: signal ?? abortController.value.signal,
        async onResponse({ response }) {
          // Sentry.addBreadcrumb({
          //   category: 'http',
          //   message: `Received response from ${requestURL}`,
          //   level: 'info',
          //   data: {
          //     status: response.status || 200,
          //     data: response,
          //   },
          // })
          if (fullResponse === true) fullRes = response
        },
        retryDelay: 1000,
      })

      appendServerProfilerLog({
        url: requestURL,
        method,
        status: 200,
        duration: Math.max(0, Date.now() - startedAt),
        sizeBytes: estimateValueSizeBytes(response),
        timestamp: new Date().toISOString(),
      })
    } catch (error) {
      appendServerProfilerLog({
        url: requestURL,
        method,
        status: Number(error?.response?.status || 0),
        duration: Math.max(0, Date.now() - startedAt),
        sizeBytes: 0,
        timestamp: new Date().toISOString(),
      })

      if (error.response) {
        switch (error.response.status) {
          case 401:
            await $auth.logout(false)
            addWarningMessage(t('Byli jste odhlášeni'), t('Odhlášení'))
            break
          case 403:
            if (recaptchaActionName) {
              passThrowRecaptchaV3.value = false
              if (recaptchaV2Key.value) {
                recaptchaV2Key.value = undefined
              }
            }
            addErrorMessage(
              error.response?._data?.errors?.[0]?.message ??
                t('Nemáte dostatečná oprávnění k tomuto zdroji'),
              t('Nedostatečná oprávnění')
            )
            break
          case 422:
            if (!customErrorHandling) {
              addWarningMessage(
                error.response._data?.errors?.[0]?.message ??
                  `${error.response.status} - ${error.response.statusText}`
              )
            }

            break
          default:
            if (error?.data.meta !== 409 && !error.response._data?.errors?.[0]?.meta)
              addErrorMessage(
                error.response._data?.errors?.[0]?.message ??
                  `${error.response.status} - ${error.response.statusText}`
              )
            break
        }
      } else {
        if (!error?.options?.signal?.aborted && error?.name !== 'AbortError') {
          addErrorMessage(t('Žádná odpověď ze serveru'))
        }
      }

      if (error?.response?._data?.errors?.[0]?.message) {
        throw error
      } else if (!error?.options?.signal?.aborted && error?.name !== 'AbortError') {
        throw createError({
          ...error,
          statusCode: error.status,
          data: { serverError: true },
        })
      }
    }

    if (recaptchaActionName && !passThrowRecaptchaV3.value) {
      passThrowRecaptchaV3.value = true
      recaptchaV2Key.value = undefined
    }

    // if (!response.data) {
    //   const message = t('V odpovědi neexistují data')
    //   // TODO: Co zobrazit uživateli?
    //   throw new Error(message)
    // }
    return fullResponse === true && fullRes
      ? fullRes
      : (response as {
          data: Type
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { total?: number; hasMorePages?: boolean; validationErrors?: any[] } & Meta
        })
  }

  /**
   * Sends a GET request to the specified URL.
   * @template Type - The expected response data type.
   * @template Meta - The expected meta data type.
   * @template SpecificType - The full response type.
   * @param {string} url - The endpoint URL.
   * @param {object} [params] - Query parameters as an object.
   * @param {object} [options] - Additional fetch options.
   * @param {object} [options.headerOptions] - Custom headers.
   * @param {AbortSignal} [options.signal] - Optional abort signal.
   * @param {boolean} [options.fullResponse] - If true, returns the full response.
   * @param {boolean} [options.customErrorHandling] - If true, disables default 422 (validation) error handling.
   * @returns {Promise<SpecificType>} The response data.
   */
  const get = async <Type, Meta = undefined, SpecificType = { data: Type; meta: object }>(
    url: string,
    params?: object,
    options: {
      origin?: string
      headerOptions?: object
      signal?: AbortSignal
      fullResponse?: boolean
      customErrorHandling?: boolean
    } = {}
  ) => {
    return (await fetch<Type, Meta>('GET', url, {
      ...options,
      params: params ? QueryString.stringify(params) : undefined,
    })) as SpecificType
  }

  /**
   * Sends a PUT request to the specified URL.
   * @template Type - The expected response data type.
   * @template Meta - The expected meta data type.
   * @param {string} url - The endpoint URL.
   * @param {object} [data] - The request payload.
   * @param {object} [options] - Additional fetch options.
   * @param {object} [options.headerOptions] - Custom headers.
   * @param {AbortSignal} [options.signal] - Optional abort signal.
   * @param {boolean} [options.fullResponse] - If true, returns the full response.
   * @param {string} [options.recaptchaActionName] - Recaptcha action name for verification.
   * @param {boolean} [options.customErrorHandling] - If true, disables default 422 (validation) error handling.
   * @returns {Promise<Type>} The response data.
   */
  const put = async <Type, Meta = undefined>(
    url: string,
    data?: object,
    options: {
      origin?: string
      headerOptions?: object
      signal?: AbortSignal
      fullResponse?: boolean
      recaptchaActionName?: string
      customErrorHandling?: boolean
    } = {}
  ) => {
    return await fetch<Type, Meta>('PUT', url, { ...options, data })
  }

  /**
   * Sends a PATCH request to the specified URL.
   * @template Type - The expected response data type.
   * @template Meta - The expected meta data type.
   * @param {string} url - The endpoint URL.
   * @param {object} [data] - The request payload.
   * @param {object} [options] - Additional fetch options.
   * @param {object} [options.headerOptions] - Custom headers.
   * @param {AbortSignal} [options.signal] - Optional abort signal.
   * @param {boolean} [options.fullResponse] - If true, returns the full response.
   * @param {string} [options.recaptchaActionName] - Recaptcha action name for verification.
   * @param {boolean} [options.customErrorHandling] - If true, disables default 422 (validation) error handling.
   * @returns {Promise<Type>} The response data.
   */
  const patch = async <Type, Meta = undefined>(
    url: string,
    data?: object,
    options: {
      origin?: string
      headerOptions?: object
      signal?: AbortSignal
      fullResponse?: boolean
      recaptchaActionName?: string
      customErrorHandling?: boolean
    } = {}
  ) => {
    return await fetch<Type, Meta>('PATCH', url, { ...options, data })
  }

  /**
   * Sends a POST request to the specified URL.
   * @template Type - The expected response data type.
   * @template Meta - The expected meta data type.
   * @param {string} url - The endpoint URL.
   * @param {object} [data] - The request payload.
   * @param {object} [options] - Additional fetch options.
   * @param {object} [options.headerOptions] - Custom headers.
   * @param {AbortSignal} [options.signal] - Optional abort signal.
   * @param {boolean} [options.fullResponse] - If true, returns the full response.
   * @param {string} [options.recaptchaActionName] - Recaptcha action name for verification.
   * @param {boolean} [options.customErrorHandling] - If true, disables default 422 (validation) error handling.
   * @returns {Promise<Type>} The response data.
   */
  const post = async <Type, Meta = undefined>(
    url: string,
    data?: object,
    options: {
      origin?: string
      headerOptions?: object
      signal?: AbortSignal
      fullResponse?: boolean
      recaptchaActionName?: string
      customErrorHandling?: boolean
    } = {}
  ) => {
    return await fetch<Type, Meta>('POST', url, { ...options, data })
  }

  /**
   * Sends a DELETE request to the specified URL.
   * @template Type - The expected response data type.
   * @template Meta - The expected meta data type.
   * @param {string} url - The endpoint URL.
   * @param {object|null} [params] - Query parameters as an object.
   * @returns {Promise<Type>} The response data.
   */
  const remove = async <Type, Meta = undefined>(url: string, params: object | null = null) => {
    const query = params ? QueryString.stringify(params) : undefined
    return await fetch<Type, Meta>('DELETE', url, { params: query })
  }

  const downloadFromResponse = async (res: {
    headers: { get: (arg0: string) => string }
    _data: BlobPart
  }) => {
    const filename = res.headers.get('Content-Disposition')?.split('=')[1]
    // This downloads file from response
    const blob = new Blob([res._data])
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    if (filename) {
      link.download = filename
    }
    link.click()
  }

  const openPrintFileInNewWindow = async (
    res: {
      headers: { get: (arg0: string) => string | null }
      _data: BlobPart
    },
    acceptType: string = 'application/pdf'
  ) => {
    try {
      // Create a Blob object from the response data with the correct MIME type
      const blob = new Blob([res._data], { type: acceptType })

      // Create a URL for the blob
      const fileURL = window.URL.createObjectURL(blob)

      // Open the blob URL in a new tab
      const newWindow = window.open(fileURL, '_blank')

      // If the new window is successfully opened, trigger the print dialog
      if (newWindow) {
        // Wait for the new window to load the content, then trigger the print dialog
        newWindow.onload = () => {
          newWindow.print()
        }
      } else {
        console.error('Failed to open the new window.')
      }
    } catch (error) {
      console.error('Error opening file:', error)
    }
  }

  return {
    get,
    put,
    post,
    patch,
    remove,
    downloadFromResponse,
    openPrintFileInNewWindow,
  }
}
